import { useEffect, useMemo, useState } from "react";
import { Button, Input, Space, message } from "antd";
import { Link } from "react-router";
import { Dialog as ZhDialog } from "zhihao-ui-react";
import {
    ProductDetailTabSection,
    ProductDetailTabTable,
    emptyProductDetailColumns,
    type ProductDetailFilterValues,
    type ProductDetailTabProps
} from "@/views/pages/productManagement/ProductDetailTabPrimitives";
import { scheduleService } from "@/api/services/scheduleService";
import { LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { dateFormat, fmtNum } from "@zhihaoscm/utils";
import { areaLib } from "@/utils/areaLib";
import { useUserStore } from "@/stores/react/authStore";
import { showRequestError } from "@/utils/requestErrorMessage";
import { RECEIVE_STATE, SCHEDULE_STATE } from "@zhihaoscm/meta";
import type { ScheduleDateVo, ScheduleForm, ScheduleVo } from "@zhihaoscm/entity";

export type indexProps = ProductDetailTabProps;

const CALENDAR_PAGE_SIZE = 15;

function addMonths(value: string, months: number): Date {
    const date = new Date(value);
    date.setMonth(date.getMonth() + months);
    return date;
}

function formatArea(product: Record<string, any>): string {
    const area = areaLib.getFullAreaName(product.provinceCode, product.cityCode, product.regionCode);
    const codes = [product.provinceCode, product.cityCode, product.regionCode].filter(Boolean).join("/");
    const address = String(product.address || "");
    // 旧 Vue 在地址已经包含地区名称时保留编码前缀，避免出现重复的省市区文本。
    if (address && area && (address.includes(area) || area.endsWith(address))) {
        return [codes, address].filter(Boolean).join(" ") || "--";
    }
    return [area, address].filter(Boolean).join(" ") || [codes, address].filter(Boolean).join(" ") || "--";
}

function createFallbackCalendar(start: string, end: string): ScheduleDateVo[] {
    const first = new Date(start);
    const last = addMonths(end, 2);
    const dates: ScheduleDateVo[] = [];
    for (const cursor = new Date(first); cursor <= last; cursor.setDate(cursor.getDate() + 1)) {
        dates.push({
            scheduleDate: cursor.toISOString(),
            scheduleVos: [],
            output: null as never,
            remainAvailablePickedUpTon: 0,
            scheduledTon: 0
        });
    }
    return dates;
}

/** components / caclenderTab / index：保留数据展示、编辑提交和父级事件回调。 */
export default function MigrationComponent(props: indexProps) {
    const product = props.data?.productConsignment || {};
    const [calendarData, setCalendarData] = useState<ScheduleDateVo[]>([]);
    const [calendarPage, setCalendarPage] = useState(0);
    const [totalTon, setTotalTon] = useState<number>(0);
    const [scheduleRows, setScheduleRows] = useState<any[]>([]);
    const [scheduleTotal, setScheduleTotal] = useState(0);
    const [scheduleFilters, setScheduleFilters] = useState<ProductDetailFilterValues>({});
    const [reloadToken, setReloadToken] = useState(0);
    const [editingSchedule, setEditingSchedule] = useState<ScheduleVo | null>(null);
    const [editingScheduleDate, setEditingScheduleDate] = useState("");
    const [creatingSchedule, setCreatingSchedule] = useState(false);
    const [newSchedule, setNewSchedule] = useState({
        scheduleDate: "",
        orderId: "",
        estimatedTon: "",
        contacts: "",
        cellphone: "",
        detailAddress: ""
    });
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const canDeal = permissions.includes("ROLE_PRODUCT_DEAL");
    const deliveryPeriod =
        product.deliveryStartTime && product.deliveryEndTime
            ? `${String(product.deliveryStartTime).slice(0, 10)} 至 ${String(product.deliveryEndTime).slice(0, 10)}`
            : "--";
    const location = formatArea(product);

    useEffect(() => {
        let disposed = false;
        const productId = product.id;
        if (!product.deliveryStartTime || !product.deliveryEndTime) {
            setCalendarData([]);
            setTotalTon(0);
            return;
        }
        // 先按提货周期绘制稳定的空排期卡片，接口稍后返回时再替换为真实分组数据。
        setCalendarData(createFallbackCalendar(product.deliveryStartTime, product.deliveryEndTime));
        if (!productId) return;
        const beginTime = dateFormat(product.deliveryStartTime, LOG_DATE_FORMAT_START);
        const endTime = dateFormat(addMonths(product.deliveryEndTime, 2), LOG_DATE_FORMAT_END);
        setCalendarPage(0);
        void Promise.all([
            scheduleService.scheduleDate({ beginTime, endTime, productId: String(productId) }),
            scheduleService.amount({ productId: String(productId) }),
            scheduleService.schedulePaging({
                page: 1,
                size: 10,
                productId: String(productId),
                ...scheduleFilters
            } as never)
        ])
            .then(([dates, amount, page]) => {
                if (disposed) return;
                setCalendarData((dates || []).map(item => ({ ...item, scheduleVos: item.scheduleVos || [] })));
                setTotalTon(Number(amount || 0));
                setScheduleTotal(Number(page?.total || page?.list?.length || 0));
                setScheduleRows(
                    ((page?.list || []) as unknown as ScheduleVo[]).map((item: ScheduleVo, index: number) => {
                        const schedule = item.schedule;
                        const orderId = schedule?.orderId;
                        const customerId = schedule?.customerId;
                        const customerLabel = [schedule?.customerEnterprise?.name, schedule?.customerEnterprise?.mobile]
                            .filter(Boolean)
                            .join(" | ");
                        const shipId = item.transportOrderShip?.shipId || schedule?.shipId;
                        const shipLabel = [schedule?.shipInfo?.cnname || schedule?.shipInfo?.name, schedule?.shipId]
                            .filter(Boolean)
                            .join(" | ");
                        const canEdit =
                            canDeal && Number(schedule?.state) === Number(SCHEDULE_STATE.TO_BE_GENERATED.code);
                        const canDelete =
                            canEdit && Number(item.receivePayments?.[0]?.state) !== Number(RECEIVE_STATE.PAID.code);
                        return {
                            id: item.schedule?.id || `schedule-${index}`,
                            排期日期: String(item.schedule?.scheduleDate || "--").slice(0, 10),
                            排期编号: item.schedule?.id || "--",
                            订单编号: orderId ? (
                                <Link to={`/tradeManage/tradeOrder/detail?id=${orderId}&time=${Date.now()}`}>
                                    {orderId}
                                </Link>
                            ) : (
                                "--"
                            ),
                            采购用户: customerId ? (
                                <Link to={`/lianYunUser/userInfo?userId=${customerId}`}>{customerLabel || "--"}</Link>
                            ) : (
                                customerLabel || "--"
                            ),
                            "预估提货吨数(吨)": item.schedule?.estimatedTon ?? "--",
                            船舶: shipId ? (
                                <Link to={`/logistics/findLocation/${shipId}?time=${Date.now()}`}>
                                    {shipLabel || shipId}
                                </Link>
                            ) : (
                                shipLabel || "--"
                            ),
                            运单: item.schedule?.transportOrderShipId || "--",
                            收货信息: item.schedule?.addressInfo?.detailAddress || "--",
                            排期状态:
                                Number(item.schedule?.state) === 5
                                    ? "待生成"
                                    : (item.schedule as Record<string, any>)?.stateName || item.schedule?.state || "--",
                            操作:
                                canEdit || canDelete ? (
                                    <Space size={0}>
                                        {canEdit ? (
                                            <Button
                                                aria-label={`修改排期-${schedule?.id}`}
                                                disabled={banButton}
                                                size="small"
                                                type="link"
                                                onClick={() => {
                                                    setEditingSchedule(item);
                                                    setEditingScheduleDate(
                                                        String(schedule?.scheduleDate || "").slice(0, 10)
                                                    );
                                                }}
                                            >
                                                修改
                                            </Button>
                                        ) : null}
                                        {canDelete ? (
                                            <Button
                                                aria-label={`删除排期-${schedule?.id}`}
                                                disabled={banButton}
                                                size="small"
                                                type="link"
                                                onClick={() => void deleteSchedule(item)}
                                            >
                                                删除
                                            </Button>
                                        ) : null}
                                    </Space>
                                ) : (
                                    ""
                                )
                        };
                    })
                );
            })
            .catch(() => {
                if (disposed) return;
                setTotalTon(0);
                setScheduleRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [
        product.id,
        product.deliveryStartTime,
        product.deliveryEndTime,
        scheduleFilters,
        reloadToken,
        canDeal,
        banButton
    ]);

    /** 删除排期后同时刷新列表和日历，保持旧 Vue 两块数据联动。 */
    const deleteSchedule = async (row: ScheduleVo) => {
        const id = row.schedule?.id;
        if (!id) return;
        try {
            await scheduleService.delete(String(id));
            message.success("删除排期成功");
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "删除排期失败");
        }
    };

    /** 修改排期沿用原排期完整字段，只替换用户在弹框中调整的日期。 */
    const submitScheduleEdit = async () => {
        const schedule = editingSchedule?.schedule;
        if (!schedule?.id || !editingScheduleDate) {
            message.error("请选择排期日期");
            return;
        }
        setScheduleSubmitting(true);
        try {
            await scheduleService.update(String(schedule.id), {
                ...schedule,
                scheduleDate: `${editingScheduleDate}T00:00:00`
            } as unknown as ScheduleForm);
            message.success("修改排期成功");
            setEditingSchedule(null);
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "修改排期失败");
        } finally {
            setScheduleSubmitting(false);
        }
    };

    /** 表头“新增排期”提交真实新增接口，避免迁移后按钮只保留外观而没有业务事件。 */
    const submitScheduleCreate = async () => {
        if (!product.id || !newSchedule.scheduleDate || !newSchedule.orderId || Number(newSchedule.estimatedTon) <= 0) {
            message.error("请填写排期日期、订单编号和预估提货吨数");
            return;
        }
        setScheduleSubmitting(true);
        try {
            await scheduleService.add({
                productId: String(product.id),
                orderId: newSchedule.orderId.trim(),
                scheduleDate: `${newSchedule.scheduleDate}T00:00:00`,
                estimatedTon: Number(newSchedule.estimatedTon),
                transportOrderShipId: "",
                shipId: "",
                addressInfo: {
                    contacts: newSchedule.contacts.trim(),
                    cellphone: newSchedule.cellphone.trim(),
                    detailAddress: newSchedule.detailAddress.trim()
                }
            } as ScheduleForm);
            message.success("新增排期成功");
            setCreatingSchedule(false);
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "新增排期失败");
        } finally {
            setScheduleSubmitting(false);
        }
    };

    const visibleCalendarData = useMemo(
        () => calendarData.slice(calendarPage * CALENDAR_PAGE_SIZE, (calendarPage + 1) * CALENDAR_PAGE_SIZE),
        [calendarData, calendarPage]
    );
    const calendarPageCount = Math.max(1, Math.ceil(calendarData.length / CALENDAR_PAGE_SIZE));
    return (
        <div className="product-detail-tab-content calender-tab">
            <ProductDetailTabSection title="提货信息">
                <div className="product-detail-delivery-metrics">
                    <div className="product-detail-delivery-metric">
                        <span>提货周期</span>
                        <strong>{deliveryPeriod}</strong>
                    </div>
                    <div className="product-detail-delivery-metric">
                        <span>提货地点</span>
                        <strong>{location}</strong>
                    </div>
                </div>
            </ProductDetailTabSection>
            <ProductDetailTabSection title="排期日历" className="product-detail-tab-calendar">
                <div className="product-detail-calendar-header">
                    <div className="product-detail-calendar-actions">
                        <Button type="primary" size="middle">
                            修改
                        </Button>
                        <div className="product-detail-calendar-date-range">开始时间 - 结束时间</div>
                        <Button type="primary" size="middle">
                            搜索
                        </Button>
                    </div>
                    <div className="product-detail-calendar-actions">
                        <Button
                            type="primary"
                            ghost
                            size="middle"
                            disabled={calendarPage <= 0}
                            onClick={() => setCalendarPage(page => Math.max(0, page - 1))}
                        >
                            上一页
                        </Button>
                        <Button
                            type="primary"
                            ghost
                            size="middle"
                            disabled={calendarPage >= calendarPageCount - 1}
                            onClick={() => setCalendarPage(page => Math.min(calendarPageCount - 1, page + 1))}
                        >
                            下一页
                        </Button>
                    </div>
                </div>
                <div className="product-detail-calendar-grid">
                    {visibleCalendarData.map(day => {
                        const date = new Date(day.scheduleDate);
                        const hasSchedule = Boolean(day.scheduleVos?.length);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const expired = date.getTime() < today.getTime();
                        return (
                            <div
                                className={`product-detail-calendar-day${expired ? " is-expired" : ""}`}
                                key={day.scheduleDate}
                            >
                                <div className="product-detail-calendar-date">
                                    <strong>{dateFormat(day.scheduleDate)}</strong>
                                    <span>{date.toLocaleDateString("zh-CN", { weekday: "short" })}</span>
                                    <span className="product-detail-calendar-scheduled">
                                        已排{fmtNum(day.scheduledTon || 0)}吨
                                    </span>
                                </div>
                                <div className={`product-detail-calendar-estimate${hasSchedule ? " is-active" : ""}`}>
                                    <span>{expired ? "不可提货" : "预估可提"}</span>
                                    {!expired && <strong>{fmtNum(day.remainAvailablePickedUpTon || 0)} 吨</strong>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {!calendarData.length && <div className="product-detail-tab-empty">暂无排期</div>}
            </ProductDetailTabSection>
            <ProductDetailTabSection title="排期列表">
                <div className="product-detail-calendar-total">
                    总排期吨数（吨）<strong>{fmtNum(totalTon)}</strong>
                </div>
                <ProductDetailTabTable
                    ariaLabel="排期列表"
                    columns={emptyProductDetailColumns([
                        "排期日期",
                        "排期编号",
                        "订单编号",
                        "采购用户",
                        "预估提货吨数(吨)",
                        "船舶",
                        "运单",
                        "收货信息",
                        "排期状态",
                        "操作"
                    ])}
                    data={scheduleRows}
                    total={scheduleTotal}
                    onSearch={setScheduleFilters}
                    onCreate={
                        canDeal && !banButton
                            ? () => {
                                  setNewSchedule({
                                      scheduleDate: new Date().toISOString().slice(0, 10),
                                      orderId: "",
                                      estimatedTon: "",
                                      contacts: "",
                                      cellphone: "",
                                      detailAddress: ""
                                  });
                                  setCreatingSchedule(true);
                              }
                            : undefined
                    }
                />
            </ProductDetailTabSection>
            <ZhDialog
                open={creatingSchedule}
                title="新增排期"
                width="920px"
                onCancel={() => {
                    if (!scheduleSubmitting) setCreatingSchedule(false);
                }}
                footer={
                    <Space>
                        <Button disabled={scheduleSubmitting} onClick={() => setCreatingSchedule(false)}>
                            取消
                        </Button>
                        <Button loading={scheduleSubmitting} type="primary" onClick={() => void submitScheduleCreate()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Input
                        aria-label="新增排期日期"
                        type="date"
                        value={newSchedule.scheduleDate}
                        onChange={event => setNewSchedule(value => ({ ...value, scheduleDate: event.target.value }))}
                    />
                    <Input
                        aria-label="新增排期订单编号"
                        placeholder="请输入订单编号"
                        value={newSchedule.orderId}
                        onChange={event => setNewSchedule(value => ({ ...value, orderId: event.target.value }))}
                    />
                    <Input
                        aria-label="新增排期预估提货吨数"
                        min="0"
                        placeholder="请输入预估提货吨数"
                        type="number"
                        value={newSchedule.estimatedTon}
                        onChange={event => setNewSchedule(value => ({ ...value, estimatedTon: event.target.value }))}
                    />
                    <Space style={{ width: "100%" }}>
                        <Input
                            aria-label="新增排期收货人"
                            placeholder="请输入收货人"
                            value={newSchedule.contacts}
                            onChange={event => setNewSchedule(value => ({ ...value, contacts: event.target.value }))}
                        />
                        <Input
                            aria-label="新增排期收货手机号"
                            placeholder="请输入收货人手机号"
                            value={newSchedule.cellphone}
                            onChange={event => setNewSchedule(value => ({ ...value, cellphone: event.target.value }))}
                        />
                    </Space>
                    <Input
                        aria-label="新增排期详细地址"
                        placeholder="请输入详细地址"
                        value={newSchedule.detailAddress}
                        onChange={event => setNewSchedule(value => ({ ...value, detailAddress: event.target.value }))}
                    />
                </Space>
            </ZhDialog>
            <ZhDialog
                open={editingSchedule !== null}
                title="修改排期"
                width="520px"
                onCancel={() => {
                    if (!scheduleSubmitting) setEditingSchedule(null);
                }}
                footer={
                    <Space>
                        <Button disabled={scheduleSubmitting} onClick={() => setEditingSchedule(null)}>
                            取消
                        </Button>
                        <Button loading={scheduleSubmitting} type="primary" onClick={() => void submitScheduleEdit()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <label className="block" htmlFor="consignment-schedule-date">
                    排期日期
                </label>
                <Input
                    id="consignment-schedule-date"
                    type="date"
                    value={editingScheduleDate}
                    onChange={event => setEditingScheduleDate(event.target.value)}
                />
            </ZhDialog>
            {props.children}
        </div>
    );
}
